import { ApolloError } from '@apollo/client';
import { reportToSentry } from '@sonnen/web-utils';
import { useAuth } from '@sonnen/web-utils/hooks';
import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../AppContext/AppContext';
import { AppRoute } from '../constants';
import { useGetCurrentUserQuery } from '../graphql/generated';
import { AppContextType, CurrentUser } from '../types';

/**
 * Hook to get current user data from backend and automatically update
 * the user in the AppContext.
 * @returns Current user from API, loading state and error
 */
export const useCurrentUserData = (): [
  CurrentUser | null | undefined,
  boolean,
  ApolloError | undefined,
] => {
  const { pathname } = useLocation();
  const { user: authUser, loading: authLoading, refreshToken } = useAuth<CurrentUser>();
  const { setUser, setUserProcessing } = useContext<AppContextType>(AppContext);

  const {
    data: userData,
    loading: userDataLoading,
    error,
    refetch,
  } = useGetCurrentUserQuery({
    skip: !authUser?.access_token || pathname === AppRoute.MAINTENANCE,
  });

  useEffect(() => {
    const userProcessed = (!authLoading && !authUser?.access_token) || userData || error;
    if (userProcessed) setUserProcessing(false);
  }, [authLoading, authUser, userData, error]);

  useEffect(() => {
    if (userData?.currentUser) setUser(userData.currentUser);
  }, [userData]);

  const reloadUserData = async () => {
    if (!authUser?.refresh_token) {
      reportToSentry('No refresh token available', {
        tag: 'auth_refresh_token',
        level: 'warning',
        context: {
          component: 'useCurrentUserData',
          pathname,
          hasAccessToken: !!authUser?.access_token,
        },
      });
      return;
    }

    setUserProcessing(true);
    try {
      await refreshToken(authUser.refresh_token);
      await refetch();
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      reportToSentry('Request to refresh user details failed', {
        tag: 'auth_refresh_token',
        level: 'error',
        context: {
          component: 'useCurrentUserData',
          pathname,
          errorMessage: errorMsg,
          hasRefreshToken: !!authUser?.refresh_token,
        },
      });
      setUser(null);
    }
    setUserProcessing(false);
  };

  useEffect(() => {
    if (
      error &&
      error.graphQLErrors.find(({ message }) => message?.includes('Forbidden resource'))
    ) {
      reportToSentry('Forbidden resource detected, attempting to reload user data', {
        tag: 'auth_forbidden_resource',
        level: 'info',
        context: {
          component: 'useCurrentUserData',
          pathname,
          hasAccessToken: !!authUser?.access_token,
        },
      });
      reloadUserData();
    }
  }, [error]);

  return [userData?.currentUser as CurrentUser, userDataLoading, error];
};
